import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSuperAdmin } from "../../src/context/SuperAdminContext";
import DateRangePicker from "../components/Daterangepicker";
import OverallReport from "../components/WorkerOnboarding/OverallReport"; 

// ─── Step definitions ──────────────────────────────────────────────────────────

const STEPS = [
  { step: 0,  },
  { step: 1,  },
  { step: 2,  },
  { step: 3,  },
  { step: 4,  },
  { step: 5,  },
];

const REPORT_MODES = [
    { id: "registered", label: "Registered Workers" },
    { id: "overall", label: "Team Performance" }, 
    { id: "steps", label: "Step-wise Count" },
];

// ─── Formatters ────────────────────────────────────────────────────────────────

const formatDisplayDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getTooltipStyle = (xPercent) => {
  let transform = "translateX(-50%)"; 
  if (xPercent < 12) {
    transform = "translateX(0%)";    
  } else if (xPercent > 88) {
    transform = "translateX(-100%)";  
  }
  return {
    left: `${xPercent}%`,
    top: 12,
    transform,
    whiteSpace: "nowrap",
  };
};

const formatRangeDisplay = (startDate, endDate) => {
  if (!startDate && !endDate) return "";
  if (startDate && !endDate) return formatDisplayDate(startDate);
  if (!startDate && endDate) return formatDisplayDate(endDate);
  return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
};

// ─── API formatters ────────────────────────────────────────────────────────────

const formatStepsResponse = (apiResponse) => {
  if (!apiResponse?.success || !apiResponse?.data?.totals) return null;
  const totals = apiResponse.data.totals;
  const counts = STEPS.map(({ step }) => ({
    step,
    count: totals[`steps_${step}`] || 0,
  }));
  return {
    total: totals.steps_0 || 0,
    counts,
    fromDate: apiResponse.data.fromDate,
    toDate: apiResponse.data.toDate,
    dateWise: apiResponse.data.dateWise || [],
  };
};

const formatRegisteredResponse = (apiResponse) => {
  if (!apiResponse?.success || !apiResponse?.data) return null;
  return {
    dateWise: apiResponse.data.dateWise || [],
    cumulativeTotal: apiResponse.data.cumulativeTotal || 0,
    fromDate: apiResponse.data.fromDate,
    toDate: apiResponse.data.toDate,
  };
};

// ─── CSV download helper ───────────────────────────────────────────────────────

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Line Chart Component ──────────────────────────────────────────────────────

const RegisteredWorkersChart = ({ dateWise, cumulativeTotal }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  if (!dateWise || dateWise.length === 0) return null;

  const W = 800;
  const H = 260;
  const padL = 48;
  const padR = 20;
  const padT = 16;
  const padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const maxVal = Math.max(...dateWise.map((d) => d.registered), 1);

  const yTickCount = 4;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) =>
    Math.round((maxVal / yTickCount) * i)
  );

  const points = dateWise.map(({ date, registered }, i) => ({
    date,
    registered,
    x: padL + (i / Math.max(dateWise.length - 1, 1)) * chartW,
    y: padT + chartH - (registered / maxVal) * chartH,
  }));

  const linePath = points
    .map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`))
    .join(" ");

  const fillPath =
    `M ${points[0].x},${padT + chartH} ` +
    points.map((p) => `L ${p.x},${p.y}`).join(" ") +
    ` L ${points[points.length - 1].x},${padT + chartH} Z`;

  const xLabelIndices = (() => {
    const n = dateWise.length;
    if (n <= 6) return dateWise.map((_, i) => i);
    const step = (n - 1) / 5;
    return [0, 1, 2, 3, 4, 5].map((i) => Math.round(i * step));
  })();

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;

    let closest = null;
    let minDist = Infinity;
    points.forEach((p) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = p;
      }
    });

    if (closest && minDist < chartW / dateWise.length) {
      setTooltip(closest);
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">
          Daily Registrations
          <span className="ml-2 text-xs font-normal text-gray-400">
            — {formatRangeDisplay(dateWise[0]?.date, dateWise[dateWise.length - 1]?.date)}
          </span>
        </p>
        <span className="text-xs text-gray-400">
          {cumulativeTotal.toLocaleString()} total registered
        </span>
      </div>

      <div className="px-2 pt-3 pb-2 relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 260 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltip(null)}
        >
          <defs>
            <linearGradient id="regFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
            </linearGradient>
          </defs>

          {yTicks.map((tick) => {
            const y = padT + chartH - (tick / maxVal) * chartH;
            return (
              <g key={tick}>
                <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                  {tick >= 1000 ? `${(tick / 1000).toFixed(1)}k` : tick}
                </text>
              </g>
            );
          })}

          <path d={fillPath} fill="url(#regFill)" />
          <path d={linePath} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {points.map(({ date, x, y }) => (
            <circle key={date} cx={x} cy={y} r="3.5" fill="white" stroke="#6366f1" strokeWidth="2" />
          ))}

          {tooltip && (
            <>
              <line x1={tooltip.x} y1={padT} x2={tooltip.x} y2={padT + chartH} stroke="#6366f1" strokeWidth="1" strokeDasharray="3,3" />
              <circle cx={tooltip.x} cy={tooltip.y} r="5" fill="#6366f1" />
            </>
          )}

          {xLabelIndices.map((idx) => {
            const p = points[idx];
            if (!p) return null;
            const d = new Date(p.date);
            const label = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
            return (
              <text key={idx} x={p.x} y={H - 8} textAnchor="middle" fontSize="11" fill="#9ca3af">
                {label}
              </text>
            );
          })}
        </svg>

        {tooltip && (
          <div
            className="absolute z-20 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg"
            style={getTooltipStyle((tooltip.x / W) * 100)}
          >
            <div className="font-medium">{formatDisplayDate(tooltip.date)}</div>
            <div className="text-indigo-300 mt-0.5">{tooltip.registered.toLocaleString()} registered</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Two Line Chart Component (Total Registered & Active Workers) ─────────────

const TotalWorkersChart = ({ dateWise }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  if (!dateWise || dateWise.length === 0) return null;

  const W = 800;
  const H = 260;
  const padL = 52;
  const padR = 20;
  const padT = 16;
  const padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const maxVal = Math.max(
    ...dateWise.map((d) => Math.max(d.totalRegistered || 0, d.totalActiveWorkers || 0)),
    1
  );

  const yTickCount = 4;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) =>
    Math.round((maxVal / yTickCount) * i)
  );

  const pointsRegistered = dateWise.map(({ date, totalRegistered }, i) => ({
    date,
    value: totalRegistered || 0,
    type: "registered",
    x: padL + (i / Math.max(dateWise.length - 1, 1)) * chartW,
    y: padT + chartH - ((totalRegistered || 0) / maxVal) * chartH,
  }));

  const pointsActive = dateWise.map(({ date, totalActiveWorkers }, i) => ({
    date,
    value: totalActiveWorkers || 0,
    type: "active",
    x: padL + (i / Math.max(dateWise.length - 1, 1)) * chartW,
    y: padT + chartH - ((totalActiveWorkers || 0) / maxVal) * chartH,
  }));

  const linePathRegistered = pointsRegistered
    .map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`))
    .join(" ");

  const linePathActive = pointsActive
    .map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`))
    .join(" ");

  const xLabelIndices = (() => {
    const n = dateWise.length;
    if (n <= 6) return dateWise.map((_, i) => i);
    const step = (n - 1) / 5;
    return [0, 1, 2, 3, 4, 5].map((i) => Math.round(i * step));
  })();

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;

    let closestIndex = null;
    let minDist = Infinity;
    pointsRegistered.forEach((p, i) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closestIndex = i;
      }
    });

    if (closestIndex !== null && minDist < chartW / dateWise.length) {
      setTooltip({
        date: dateWise[closestIndex].date,
        registered: dateWise[closestIndex].totalRegistered || 0,
        active: dateWise[closestIndex].totalActiveWorkers || 0,
        x: pointsRegistered[closestIndex].x,
        yRegistered: pointsRegistered[closestIndex].y,
        yActive: pointsActive[closestIndex].y,
      });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">
          Total Workers Over Time
          <span className="ml-2 text-xs font-normal text-gray-400">
            — {formatRangeDisplay(dateWise[0]?.date, dateWise[dateWise.length - 1]?.date)}
          </span>
        </p>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-gray-500">Total Registered</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-gray-500">Active Workers</span>
          </div>
        </div>
      </div>

      <div className="px-2 pt-3 pb-2 relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 260 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltip(null)}
        >
          {yTicks.map((tick) => {
            const y = padT + chartH - (tick / maxVal) * chartH;
            return (
              <g key={tick}>
                <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                  {tick >= 1000 ? `${(tick / 1000).toFixed(1)}k` : tick}
                </text>
              </g>
            );
          })}

          {/* Total Registered Line */}
          <path
            d={linePathRegistered}
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Active Workers Line */}
          <path
            d={linePathActive}
            fill="none"
            stroke="#f59e0b"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Data points for Total Registered */}
          {pointsRegistered.map(({ date, x, y }) => (
            <circle key={`reg-${date}`} cx={x} cy={y} r="3.5" fill="white" stroke="#10b981" strokeWidth="2" />
          ))}

          {/* Data points for Active Workers */}
          {pointsActive.map(({ date, x, y }) => (
            <circle key={`act-${date}`} cx={x} cy={y} r="3.5" fill="white" stroke="#f59e0b" strokeWidth="2" />
          ))}

          {tooltip && (
            <>
              <line
                x1={tooltip.x}
                y1={padT}
                x2={tooltip.x}
                y2={padT + chartH}
                stroke="#6b7280"
                strokeWidth="1"
                strokeDasharray="3,3"
              />
              <circle cx={tooltip.x} cy={tooltip.yRegistered} r="5" fill="#10b981" />
              <circle cx={tooltip.x} cy={tooltip.yActive} r="5" fill="#f59e0b" />
            </>
          )}

          {xLabelIndices.map((idx) => {
            const p = pointsRegistered[idx];
            if (!p) return null;
            const d = new Date(p.date);
            const label = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
            return (
              <text key={idx} x={p.x} y={H - 8} textAnchor="middle" fontSize="11" fill="#9ca3af">
                {label}
              </text>
            );
          })}
        </svg>

        {tooltip && (
          <div
            className="absolute z-20 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg"
            style={getTooltipStyle((tooltip.x / W) * 100)}
          >
            <div className="font-medium mb-1.5">{formatDisplayDate(tooltip.date)}</div>
            <div className="flex items-center gap-1.5 text-emerald-300">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              <span>Total Registered: {tooltip.registered.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5 text-amber-300 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-amber-400"></div>
              <span>Active Workers: {tooltip.active.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Shared empty / error / loading states ─────────────────────────────────────

const StateCard = ({ icon, title, subtitle, action }) => (
  <div className="bg-white border border-gray-200 rounded-xl py-20 flex flex-col items-center justify-center text-center">
    <div className="text-gray-300 mb-3">{icon}</div>
    <p className="text-gray-500 text-sm font-medium">{title}</p>
    {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
    {action}
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const PerformanceReport = () => {
  const { token, downloadWorkerStepsCSV, fetchRegisteredWorkersCount, downloadRegisteredWorkersCSV } = useSuperAdmin();

  const [activeMode, setActiveMode] = useState("registered");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Steps report state
  const [stepsData, setStepsData] = useState(null);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [stepsError, setStepsError] = useState(null);
  const [downloadingCSV, setDownloadingCSV] = useState(false);

  // Registered workers state
  const [regData, setRegData] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState(null);
  const [downloadingRegCSV, setDownloadingRegCSV] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setEndDate(end.toISOString().split("T")[0]);
    setStartDate(start.toISOString().split("T")[0]);
  }, []);

  // ── Fetch steps report ────────────────────────────────────────────────────

  const fetchStepsData = useCallback(async () => {
    if (!startDate || !endDate || !token) {
      setStepsData(null);
      return;
    }
    setStepsLoading(true);
    setStepsError(null);
    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${BASE_URL}/ondemand-worker-reports/daily-worker-steps-count?fromDate=${startDate}&toDate=${endDate}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const formatted = formatStepsResponse(result);
      if (!formatted) throw new Error("Invalid response format from server");
      setStepsData(formatted);
    } catch (err) {
      console.error("Failed to fetch steps data:", err);
      setStepsError(err.message || "Failed to load report data");
      setStepsData(null);
    } finally {
      setStepsLoading(false);
    }
  }, [startDate, endDate, token]);

  // ── Fetch registered workers ──────────────────────────────────────────────

  const fetchRegData = useCallback(async () => {
    if (!startDate || !endDate || !token) {
      setRegData(null);
      return;
    }
    setRegLoading(true);
    setRegError(null);
    try {
      const result = await fetchRegisteredWorkersCount(startDate, endDate);
      const formatted = formatRegisteredResponse(result);
      if (!formatted) throw new Error("Invalid response format from server");
      setRegData(formatted);
    } catch (err) {
      console.error("Failed to fetch registered workers data:", err);
      setRegError(err.message || "Failed to load registered workers data");
      setRegData(null);
    } finally {
      setRegLoading(false);
    }
  }, [startDate, endDate, token, fetchRegisteredWorkersCount]);

  useEffect(() => {
    if (activeMode === "steps") {
      fetchStepsData();
    } else if (activeMode === "registered") {
      fetchRegData();
    }
    // "overall" mode handles its own fetching internally
  }, [activeMode, fetchStepsData, fetchRegData]);

  const handleDateRangeChange = ({ startDate: newStart, endDate: newEnd }) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const stepsAllZero = stepsData?.counts?.every((item) => item.count === 0);
  const regEmpty = !regData?.dateWise?.length;

  const showStepsDownload =
    activeMode === "steps" &&
    stepsData?.counts?.length > 0 &&
    !stepsAllZero;

  const showRegDownload =
    activeMode === "registered" &&
    regData?.dateWise?.length > 0 &&
    !regEmpty;

  // Whether to show the shared date picker in the header
  // (Overall Report has its own date picker inside the component)
  const showHeaderDatePicker = activeMode !== "overall";

  const handleDownloadCSV = async () => {
    if (!startDate || !endDate) return;
    setDownloadingCSV(true);
    try {
      const blob = await downloadWorkerStepsCSV(startDate, endDate);
      downloadBlob(blob, `worker_steps_${startDate}_to_${endDate}.csv`);
    } catch (err) {
      console.error("Failed to download CSV:", err);
      setStepsError(err.message || "Failed to download CSV file");
      setTimeout(() => setStepsError(null), 3000);
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleDownloadRegCSV = async () => {
    if (!startDate || !endDate) return;
    setDownloadingRegCSV(true);
    try {
      const blob = await downloadRegisteredWorkersCSV(startDate, endDate);
      downloadBlob(blob, `registered_workers_${startDate}_to_${endDate}.csv`);
    } catch (err) {
      console.error("Failed to download registered workers CSV:", err);
      setRegError(err.message || "Failed to download CSV file");
      setTimeout(() => setRegError(null), 3000);
    } finally {
      setDownloadingRegCSV(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 pt-16">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Worker Onboarding Performance</h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Shared date picker — hidden when overall tab is active */}
            {showHeaderDatePicker && (
              <div className="w-64">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  maxDate={today}
                  placeholder="Select date range"
                />
              </div>
            )}

            {/* CSV download — steps mode */}
            {showStepsDownload && (
              <button
                onClick={handleDownloadCSV}
                disabled={downloadingCSV || stepsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingCSV ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                    <span>Downloading…</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download CSV</span>
                  </>
                )}
              </button>
            )}

            {/* CSV download — registered workers mode */}
            {showRegDownload && (
              <button
                onClick={handleDownloadRegCSV}
                disabled={downloadingRegCSV || regLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingRegCSV ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                    <span>Downloading…</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download CSV</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ── Report mode toggle ── */}
        <div className="max-w-5xl mx-auto mt-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1">
            {REPORT_MODES.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveMode(id)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-150 ${
                  activeMode === id
                    ? "bg-white text-indigo-700 shadow-sm border border-gray-200"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* ════════ STEPS REPORT ════════ */}
        {activeMode === "steps" && (
          <>
            {stepsLoading && (
              <StateCard
                icon={<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />}
                title="Loading report data…"
              />
            )}

            {stepsError && !stepsLoading && (
              <div className="bg-red-50 border border-red-200 rounded-xl py-12 flex flex-col items-center justify-center text-center">
                <svg className="w-10 h-10 text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 text-sm font-medium">Failed to load report</p>
                <p className="text-red-400 text-xs mt-1">{stepsError}</p>
                <button
                  onClick={fetchStepsData}
                  className="mt-4 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {!stepsLoading && !stepsError && (!startDate || !endDate) && (
              <StateCard
                icon={
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                title="Select a date range to view the performance report"
                subtitle="Step-wise breakdown will appear here"
              />
            )}

            {!stepsLoading && !stepsError && startDate && endDate && stepsData && stepsAllZero && (
              <StateCard
                icon={
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                title="No workers found"
                subtitle="No workers registered in the selected date range"
              />
            )}

            {!stepsLoading && !stepsError && stepsData?.counts?.length > 0 && !stepsAllZero && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    Step Breakdown
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      — {formatRangeDisplay(startDate, endDate)}
                    </span>
                  </p>
                  <span className="text-xs text-gray-400">
                    {stepsData.total.toLocaleString()} workers registered
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {STEPS.map(({ step, label }) => (
                          <th
                            key={step}
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide border-r border-gray-100 last:border-r-0"
                          >
                            <div>Step {step}</div>
                            <div className="font-normal normal-case text-gray-400 mt-0.5">{label}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {stepsData.counts.map(({ step, count }) => (
                          <td
                            key={step}
                            className="px-6 py-5 text-center text-2xl font-semibold text-gray-900 border-r border-gray-100 last:border-r-0"
                          >
                            {count.toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {stepsData.dateWise?.length > 0 && (
                  <div className="border-t border-gray-200">
                    <div className="px-5 py-3 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700">Daily Breakdown</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                            {STEPS.map(({ step }) => (
                              <th key={step} className="px-4 py-2 text-center text-xs font-medium text-gray-500">
                                Step {step}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {stepsData.dateWise.map((day, idx) => (
                            <tr key={idx} className="border-t border-gray-100">
                              <td className="px-4 py-2 text-sm text-gray-600">{formatDisplayDate(day.date)}</td>
                              {STEPS.map(({ step }) => (
                                <td key={step} className="px-4 py-2 text-center text-sm text-gray-600">
                                  {day[`steps_${step}`]?.toLocaleString() || 0}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════ REGISTERED WORKERS REPORT ════════ */}
        {activeMode === "registered" && (
          <>
            {regLoading && (
              <StateCard
                icon={<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />}
                title="Loading registration data…"
              />
            )}

            {regError && !regLoading && (
              <div className="bg-red-50 border border-red-200 rounded-xl py-12 flex flex-col items-center justify-center text-center">
                <svg className="w-10 h-10 text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 text-sm font-medium">Failed to load registration data</p>
                <p className="text-red-400 text-xs mt-1">{regError}</p>
                <button
                  onClick={fetchRegData}
                  className="mt-4 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {!regLoading && !regError && (!startDate || !endDate) && (
              <StateCard
                icon={
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                title="Select a date range to view registration data"
                subtitle="A bar chart of daily registrations will appear here"
              />
            )}

            {!regLoading && !regError && startDate && endDate && regData && regEmpty && (
              <StateCard
                icon={
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                title="No registrations found"
                subtitle="No workers registered in the selected date range"
              />
            )}

            {!regLoading && !regError && regData && !regEmpty && (
              <>
                <RegisteredWorkersChart
                  dateWise={regData.dateWise}
                  cumulativeTotal={regData.cumulativeTotal}
                />
                <TotalWorkersChart dateWise={regData.dateWise} />
              </>
            )}
          </>
        )}

        {/* ════════ OVERALL REPORT ════════ */}
        {activeMode === "overall" && <OverallReport />}

      </div>
    </div>
  );
};

export default PerformanceReport;